/* eslint-disable no-useless-escape */
/**
 * Format: Product
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API product responses.
 *
 */
import { formatImages } from '~/format/format-image'
import { formatMedia } from '~/format/format-media'
import { formatOptions } from '~/format/format-option'
import { formatProductPriceRange } from '~/format/format-price'
import { formatVariants } from '~/format/format-variant'
import { convertToHandle } from '~/helpers/convert'
import { decodeApiId, getArrayFromString, richtextToHtml, transformEdges } from '~/helpers/graphql'

/**
 * Format product.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product
 * @param {Object} response - GraphQL response.
 * @param {Object} [collection] - Formatted collection object.
 * @returns {Object}
 */
export function formatProduct(response, collection) {
  const product = response?.product ? response.product : response

  let formattedProduct = formatBasicInfo(product, collection)

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct = {
    ...formattedProduct,
    ...formatFeaturedImages(product),
    ...formatVariantsAndOptions(product, formattedProduct),
    ...formatMetafields(product),
  }

  /**
   * Collection handle array.
   */
  formattedProduct.collections = transformEdges(product?.collections).map((collectionObject) => {
    return collectionObject.handle
  })

  /**
   * Preorder.
   * - Convert date into ISO format (YYYY-MM-DD) from MM-DD-YYYY en
   * - Convert date into ISO format (YYYY-MM-DD) from DD/MM/YYYY for others
   */
  const preorder = {
    date: null,
    skus: null,
  }

  let preorderField = null
  let preorderSkuField = null
  if (window.cnvs.store.permanentDomain === '//veronica-beard-canada.myshopify.com/') {
    preorderField = product?.caPreorderDate?.value
    preorderSkuField = product?.caPreorderSkus?.value
  } else {
    preorderField = product?.preorderDate?.value
    preorderSkuField = product?.preorderSkus?.value
  }

  if (preorderField) {
    const language = window.cnvs.store.language
    const splitChar = language === 'en' ? '-' : '/'
    const parts = preorderField.split(splitChar)

    if (language === 'en') {
      preorder.date = `${parts[2]}-${parts[0]}-${parts[1]}`
    } else {
      preorder.date = `${parts[2]}-${parts[1]}-${parts[0]}`
    }
    preorder.skus = preorderSkuField?.split(',').map((sku) => sku.trim())
  }

  formattedProduct.preorder = preorder

  /**
   * Format product extensions and grouping.
   */
  formattedProduct.extensions = formatProductExtensions(product)
  formattedProduct.grouping = formatProductGrouping(product)
  formattedProduct.currentSwatch = productGroupingFromMetafields(product)

  /**
   * Format legacy swatches.
   */
  formattedProduct.legacySwatches = formatLegacySwatches(product)

  return formattedProduct
}

/**
 * Initially format basic product info before handling images and metafields.
 * @param {Object} product
 * @param {Object} [collection] - Formatted collection object.
 */
function formatBasicInfo(response, collection) {
  const product = response?.product ? response.product : response

  const formattedProduct = {
    available: product?.available,
    compareAtPrice: formatProductPriceRange(product?.compareAtPriceRange),
    description: product?.descriptionHtml,
    giftCard: product?.giftCard,
    handle: product?.handle,
    id: decodeApiId(product?.id),
    media: formatMedia(product),
    price: formatProductPriceRange(product?.priceRange),
    tags: product?.tags,
    title: product?.title,
    type: product?.type,
    url: formatUrl(product?.handle, collection?.handle, product?.trackingParameters),
    variants: formatVariants(product, collection),
    vendor: product?.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product?.featuredImages))

  formattedProduct.featuredImage = featuredImages.length
    ? featuredImages[0]
    : featuredImages

  formattedProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  formattedProduct.options = formatOptions(product, formattedProduct.variants)

  formattedProduct.selectedOrFirstAvailableVariant =
    getSelectedOrFirstAvailableVariant(formattedProduct)

  return formattedProduct
}

/**
 * Get selected or first available variant.
 * - 'Selected' based on URL parameter.
 * - If no variants available then return first.
 * @param {Object} product - Formatted product object.
 * @returns {Object}
 */
function getSelectedOrFirstAvailableVariant(product) {
  const urlSearchParams = new URLSearchParams(location.search)

  const variantId = urlSearchParams.has('variant')
    ? urlSearchParams.get('variant')
    : false

  const selectedVariant = product?.variants.find((variant) => {
    if (variantId) {
      return variant.id === Number(variantId)
    }

    return variant.available
  })

  if (selectedVariant) {
    return selectedVariant
  }

  return product?.variants[0]
}

/**
 * Format product's featured and secondary images.
 * @param {Object} product
 * @returns {Object} Object with images to merge into product object.
 */
function formatFeaturedImages(product) {
  const featuredImages = formatImages(product, transformEdges(product?.featuredImages))

  const featuredImage = featuredImages.length
    ? featuredImages[0]
    : featuredImages

  const secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  return {
    featuredImage,
    secondaryImage,
  }
}

/**
 * Format product metafields.
 * @param {Object} product - Unformatted product
 * @returns {Object} Object with formatted values to merge into product object.
 */
function formatMetafields(product) {
  const metafields = {
    bottomBadge: product?.bottomBadge?.value,
    discountCode: product?.discountCode?.value,
    fitNotes: product?.fitNotes?.value
      ? richtextToHtml(product?.fitNotes?.value)
      : null,
    fitGraphicOverallFit: product?.fitGraphicOverallFit?.value || null,
    fitGraphicScale: product?.fitGraphicScale?.value || null,
    fitGraphicLow: product?.fitGraphicLow?.value || null,
    fitGraphicMiddle: product?.fitGraphicMiddle?.value || null,
    fitGraphicHigh: product?.fitGraphicHigh?.value || null,
    secondaryName: product?.secondaryName?.value
      ? richtextToHtml(product?.secondaryName?.value)
      : null,
    specialtyBadge: product?.specialtyBadge?.value,
    specialtyBadgeUrl: product?.specialtyBadgeUrl?.value,
    themeColor: product?.themeColor?.value,
    topBadge: product?.topBadge?.value,
  }

  metafields.flatImageIndex = product?.flatImageIndex
    ? Number(product?.flatImageIndex.value)
    : Number(cnvs.settings.product.flatImageIndex)

  metafields.matchingSet = {
    product: product?.matchingSetProduct?.value || null,
    description: product?.matchingSetDescription?.value || null,
    button: product?.matchingSetButton?.value || null,
  }

  metafields.dickeyJacketDescription = product?.dickeyBundleJacketDescription?.value || null
  metafields.categoryLabel = product?.categoryLabel?.value || null

  return { metafields }
}

/**
 * Format variants and options for a product.
 * @param {Object} unformattedProduct - Original response product object
 * @param {Object} formattedProduct - Product object with formatting so far
 * @returns {Object} Object with formatted values to merge into product object.
 */
function formatVariantsAndOptions(unformattedProduct, formattedProduct) {
  const hasOnlyDefaultVariant = formattedProduct?.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  const options = formatOptions(unformattedProduct, formattedProduct.variants)

  const selectedOrFirstAvailableVariant =
    getSelectedOrFirstAvailableVariant(formattedProduct)

  return {
    hasOnlyDefaultVariant,
    options,
    selectedOrFirstAvailableVariant,
  }
}

/**
 * Format response URL with tracking parameters.
 * - https://shopify.dev/docs/api/storefront/2024-01/objects/Product#field-product-trackingparameters
 * @param {String} productHandle - Product handle
 * @param {Null|String} collectionHandle - Collection handle
 * @param {Null|String} [trackingParameters] - Response tracking parameters.
 * @returns {String}
 */
export function formatUrl(productHandle, collectionHandle, trackingParameters) {
  const url = collectionHandle
    ? `${cnvs.routes.normalisedRoot}collections/${collectionHandle}/products/${productHandle}`
    : `${cnvs.routes.normalisedRoot}products/${productHandle}`

  if (!trackingParameters) {
    return url
  }

  /**
   * Storefront API doesn't include https in URL but new URL() requires it.
   */
  const localUrl = `https://${url.split('//')[1]}`
  const urlObject = new URL(localUrl)
  const trackingSearchParams = new URLSearchParams(trackingParameters)

  const parameters = new URLSearchParams({
    ...Object.fromEntries(urlObject.searchParams),
    ...Object.fromEntries(trackingSearchParams),
  })

  if (!parameters.size) {
    return url
  }

  const href = urlObject.href
    .split('?')[0]
    .replace('https:', '')

  return `${href}?${parameters.toString()}`
}

/**
 * Format product extensions (alt attributes).
 * @param {Object} product - Unformatted product response.
 * @returns {Array}
 */
function formatProductExtensions(product) {
  const extensions = product?.extensions?.references

  if (!extensions?.edges?.length) {
    return []
  }

  return transformEdges(extensions).map((reference) => {
    const name = reference?.fields?.find((field) => field.key === 'name')
    const title = reference?.fields?.find((field) => field.key === 'title')

    const nestedProductField = reference?.fields?.find((field) => field.key === 'product')
    const nestedProduct = nestedProductField?.reference
    const groupingProductsField = nestedProduct?.grouping?.reference?.fields?.find((field) => field.key === 'products')
    const groupingProducts = transformEdges(groupingProductsField?.references)

    /**
     * If no grouping value then pass parent product.
     * - This replicates Liquid behaviour.
     */
    const grouping = groupingProducts.length
      ? groupingProducts.map((groupingReference) => {
        return formatNestedProduct(groupingReference, { extension: name?.value, swatch: true })
      })
      : [formatNestedProduct(product, { swatch: true })]

    return {
      name: name?.value,
      grouping,
      product: formatNestedProduct(nestedProduct),
      title: title?.value,
    }
  })
}

/**
  * Get swatches array for product color options
  *
  * @param {*} product
  * @returns {Array} - Swatches array
  */
function productGroupingFromMetafields(product) {
  const image = product?.swatchImage || product?.swatchImages
  const colour = ['Color', 'Colour', 'Couleur'].includes(product?.options?.[0]?.name)
    ? product?.options?.[0]?.values?.[0]
    : false
  const swatchLabel = product?.swatchLabel?.value
  const swatchFile = product?.swatchFile?.reference?.image?.url
  const name = product?.swatchName || product?.swatchNames
  const swatchName = colour || swatchLabel || getArrayFromString(name?.value)[0]
  const swatchNameHandle = convertToHandle(swatchName)
  const swatchFileName = getArrayFromString(image?.value)[0]

  if ((!swatchFile || !swatchFileName) || !swatchName) {
    return []
  }

  return [
    {
      handle: product?.handle,
      id: product?.id,
      liquid: false,
      available: product?.available,
      render: true,
      swatch: {
        file: swatchFile,
        label: swatchLabel,
        name: swatchName,
        handle: swatchNameHandle,
        src: swatchFile || cnvs.urls.files.replace('placeholder', swatchFileName),
      },
    },
  ]
}

/**
 * Format product grouping (colour swatches).
 * @param {Object} product - Unformatted product response.
 * @returns {Array}
 */
function formatProductGrouping(product) {
  const grouping = product?.grouping?.reference?.fields?.find((field) => {
    return field.key === 'products'
  })

  /**
   * If no grouping value then pass parent product.
   * - This replicates Liquid behaviour.
   */
  if (!grouping) {
    return [formatNestedProduct(product, { swatch: true })]
  }

  return transformEdges(grouping.references).map((reference) => {
    return formatNestedProduct(reference, { swatch: true })
  })
}

/**
 * Format nested product from product extension or grouping metafield.
 * @param {Object} product - Extension or group product.
 * @param {Number} index - Index for unique handles.
 * @param {Object} [config] - Additional config.
 * @param {String} [config.extension] - Extension name.
 * @param {Boolean} [config.swatch] - Output swatch data.
 * @returns {Object}
 */
function formatNestedProduct(product, { extension, swatch } = {}) {

  /**
   * Do not render if unavailable and has final sale tag.
   */
  let render = true

  const finalSale = ['finalsale', 'final sale', 'final_sale', 'final-sale'].some((saleTag) => {
    return product?.tags?.find((tag) => tag.toLowerCase() === saleTag)
  })

  if (!product?.available && finalSale) {
    render = false
  }

  const formattedObject = {
    available: product?.available,
    handle: product?.handle,
    id: decodeApiId(product?.id),
    render,
    type: 'extension',
  }

  /**
   * Swatch data.
   */
  if (swatch) {
    const variants = transformEdges(product?.variants)
    const sku = convertToHandle(variants?.[0]?.sku)

    const colour = ['Color', 'Colour', 'Couleur'].includes(product?.options?.[0]?.name)
      ? product?.options?.[0]?.values?.[0]
      : false

    const swatchLabel = product?.swatchLabel?.value
    const swatchFile = product?.swatchFile?.reference?.image?.url
    const swatchName = colour || swatchLabel || getArrayFromString(product?.swatchName?.value)[0]
    const swatchNameHandle = `${convertToHandle(swatchName)}-${sku}`
    const swatchFileName = getArrayFromString(product?.swatchImage?.value)[0]

    formattedObject.swatch = {
      file: swatchFile,
      label: swatchLabel,
      name: swatchName,
      handle: swatchNameHandle,
      src: swatchFile || cnvs.urls.files.replace('placeholder', swatchFileName),
    }

    formattedObject.type = 'group'
  }

  if (extension) {
    formattedObject.extension = extension
  }

  return formattedObject
}

/**
 * Format legacy swatches.
 * @param {Object} product - Unformatted product response.
 * @returns {Array}
 */
function formatLegacySwatches(product) {
  if (product.swatchLabel || !product?.swatchNames?.value) {
    return []
  }

  const swatchNames = getArrayFromString(product?.swatchNames?.value)
  const swatchImages = getArrayFromString(product?.swatchImages?.value)

  if (swatchNames?.length < 2) {
    return []
  }

  const finalSale = ['finalsale', 'final sale', 'final_sale', 'final-sale'].some((saleTag) => {
    return product?.tags?.find((tag) => tag.toLowerCase() === saleTag)
  })

  return swatchNames.map((swatchName, index) => {
    const swatchNameHandle = convertToHandle(swatchName)
    const swatchFileName = swatchImages[index]

    return {
      available: product?.available,
      finalSale,
      handle: product?.handle,
      id: product?.id,
      render: true,
      swatch: {
        name: swatchName,
        handle: swatchNameHandle,
        src: cnvs.urls.files.replace('placeholder', swatchFileName),
      },
      type: 'legacy',
    }
  })
}
